import {Grid} from '@material-ui/core';
import {PhoneFormatCustom, SsnFormatCustom, SwitchCustom, TextBox} from '../../../Core/UI';
import React, {useCallback, useEffect} from 'react';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../CategoryCard';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {emailValidator, phoneValidator, ssnValidator} from '../../../Core/helpers/validators';
import RootStore from '../../../Core/mobx/RootStore';

type PermissionsProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};

const Permissions = observer(({valueChanged, parameters, setCategory, index}: PermissionsProps) => {
    const {
        parameterStore: {onValueChanged},
    } = RootStore.agentPortalStore;

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    useEffect(() => {
        const familyContactEmail = parameters.familyContactEmail?.value;
        const familyContactPhoneNumber = parameters.familyContactPhoneNumber?.value;

        if (familyContactPhoneNumber && familyContactEmail) {
            const value = `${familyContactPhoneNumber}, ${familyContactEmail}`;
            onValueChanged(null, parameters.familyContact, value);
        }
    }, [parameters.familyContactPhoneNumber.value, parameters.familyContactEmail.value]);

    const phoneValue = useCallback(() => {
        const rawValue = parameters.familyContactPhoneNumber.value;
        if (!rawValue) return rawValue;

        const numericValue = rawValue.replace(/[^0-9]/g, '');
        return numericValue.slice(-10);
    }, [parameters.familyContactPhoneNumber.value]);

    return (
        <CategoryCard title={'Permissions'} setCategory={setCategory} index={index}>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'medicalHistoryPermission',
                        label: 'Do you agree to allow the life insurance company to look into previous medical history to verify insurability?',
                        ...parameters.medicalHistoryPermission,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'submissionPermission',
                        label: 'Do you agree to allow Amplify to submit this application on your behalf to life insurance companies in order to get approved and secure your health rating?',
                        ...parameters.submissionPermission,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'acknowlegment',
                        label: 'Do you acknowledge that all answers to the questions stated are correct to the best of your knowledge and that any misinformation can result in non-insurability?',
                        ...parameters.acknowlegment,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={3}>
                <TextBox
                    error={
                        !!parameters.socialSecurityNumber?.value &&
                        !ssnValidator(parameters.socialSecurityNumber?.value)
                    }
                    id={'socialSecurityNumber'}
                    label={required('SSN')}
                    value={parameters.socialSecurityNumber?.value}
                    onChange={(value) => valueChanged(parameters.socialSecurityNumber, value)}
                    placeholder="394739873"
                    InputProps={{
                        inputComponent: SsnFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={3}>
                <TextBox
                    error={!!parameters.familyContactPhoneNumber.value && !phoneValidator(phoneValue())}
                    id={'familyContactPhoneNumber'}
                    label={'Family Phone Number'}
                    value={phoneValue()}
                    onChange={(value) => valueChanged(parameters.familyContactPhoneNumber, value)}
                    placeholder="(323) 232-3123"
                    InputProps={{
                        inputComponent: PhoneFormatCustom,
                    }}
                />
                <small>
                    If the lead is applying for life insurance for a family member and doesn't know their SSN, please
                    ask them for the applicant's phone number and give them a call. What is the applicant's phone number
                    and email address?
                </small>
            </Grid>
            <Grid item xs={3}>
                <TextBox
                    error={
                        !!parameters.familyContactEmail.value && !emailValidator(parameters.familyContactEmail.value)
                    }
                    id={'familyContactEmail'}
                    label={'Family Email'}
                    value={parameters.familyContactEmail?.value}
                    onChange={(value) => valueChanged(parameters.familyContactEmail, value)}
                    placeholder="example@gmail.com"
                />
            </Grid>
            <Grid item xs={3}>
                <TextBox
                    id="familyContact"
                    label="Applicant phone & email"
                    value={parameters.familyContact.value}
                    disabled={true}
                />
            </Grid>
        </CategoryCard>
    );
});

export default Permissions;
